/**
 * Camera lifecycle decisions, as pure functions.
 *
 * These are separated from the DOM work in `camera.ts` for one reason: the rules that
 * decide "is this camera actually producing a picture?" and "should we tear it down and
 * try again?" are the part that was wrong in production, and they need to be testable
 * without a browser.
 *
 * The bug they exist to prevent: on iOS, `getUserMedia` resolves, the track reports
 * `readyState: 'live'`, and the video element still shows a black rectangle forever
 * because no frames ever arrive. A live track is not a live picture.
 */

/** HTMLMediaElement.HAVE_CURRENT_DATA — the first state at which a frame exists. */
export const MIN_READY_STATE = 2;

/** How many acquire+attach cycles before giving up and asking the user. */
export const MAX_ATTACH_ATTEMPTS = 3;

/** How long to wait for a first frame before calling an attach dead. */
export const ATTACH_TIMEOUT_MS = 4000;

/** Ceiling on automatic restarts, so a genuinely broken camera cannot loop forever. */
export const MAX_AUTO_RESTARTS = 3;

export interface VideoSnapshot {
  hasVideoElement: boolean;
  hasStream: boolean;
  videoWidth: number;
  videoHeight: number;
  /** HTMLMediaElement.readyState */
  readyState: number;
  paused: boolean;
  /** MediaStreamTrack.readyState, or null when there is no video track. */
  trackReadyState: 'live' | 'ended' | null;
  /** MediaStreamTrack.muted — true while iOS has the capture suspended. */
  trackMuted: boolean | null;
}

export const EMPTY_SNAPSHOT: VideoSnapshot = {
  hasVideoElement: false,
  hasStream: false,
  videoWidth: 0,
  videoHeight: 0,
  readyState: 0,
  paused: true,
  trackReadyState: null,
  trackMuted: null,
};

/**
 * Real pixels, not just an element.
 *
 * `videoWidth`/`videoHeight` stay at 0 until the first frame is decoded, which makes
 * them the only trustworthy signal that a picture exists. Everything else — the promise
 * resolving, the track being 'live', `play()` not throwing — can all be true over black.
 */
export function hasUsableDimensions(snapshot: {
  videoWidth: number;
  videoHeight: number;
}): boolean {
  const { videoWidth, videoHeight } = snapshot;
  return (
    Number.isFinite(videoWidth) &&
    Number.isFinite(videoHeight) &&
    videoWidth > 0 &&
    videoHeight > 0
  );
}

/** The condition under which it is honest to show the supervisor a "live" viewfinder. */
export function isFrameReady(snapshot: VideoSnapshot): boolean {
  return hasUsableDimensions(snapshot) && snapshot.readyState >= MIN_READY_STATE;
}

/**
 * The black-viewfinder state: we hold a stream, the track claims to be live, and yet
 * no frame has ever arrived. Also covers a track that has ended underneath us.
 */
export function isDeadStream(snapshot: VideoSnapshot): boolean {
  if (!snapshot.hasStream) return false;
  if (snapshot.trackReadyState === 'ended') return true;
  if (snapshot.trackReadyState === 'live' && !hasUsableDimensions(snapshot)) return true;
  return false;
}

/**
 * Whether returning to the foreground requires rebuilding the camera.
 *
 * iOS suspends capture when the app is backgrounded, the phone is locked, or a call
 * arrives. What comes back varies by iOS version: sometimes the track is `muted`,
 * sometimes `ended`, sometimes it is nominally live but never delivers another frame
 * and the element sits paused. All of those need a restart, so this errs toward
 * restarting — a needless restart costs a second, a missed one costs the inspection.
 */
export function shouldRestartOnForeground(snapshot: VideoSnapshot): boolean {
  if (!snapshot.hasVideoElement || !snapshot.hasStream) return true;
  if (snapshot.trackReadyState !== 'live') return true;
  if (snapshot.trackMuted === true) return true;
  if (!hasUsableDimensions(snapshot)) return true;
  if (snapshot.paused) return true;
  return false;
}

/** Attach retries within one startup sequence. */
export function canRetryAttach(attempt: number, cap: number = MAX_ATTACH_ATTEMPTS): boolean {
  return attempt + 1 < cap;
}

/** Automatic restarts across the life of the capture screen. */
export function canAutoRestart(restarts: number, cap: number = MAX_AUTO_RESTARTS): boolean {
  return restarts < cap;
}

/**
 * Progressive constraints.
 *
 * Attempt 0 asks only for the rear camera. Pinning a resolution up front is what makes
 * iPhones hand back a stream that never delivers a frame: WebKit will accept an `ideal`
 * size, pick a capture format that cannot satisfy it, and then produce nothing.
 *
 * `facingMode` is always `ideal`, never `exact` — a device with only a front camera must
 * still be able to work rather than failing outright.
 */
export function constraintsForAttempt(
  attempt: number,
  rearDeviceId?: string,
): MediaStreamConstraints {
  if (attempt <= 0) {
    return { audio: false, video: { facingMode: { ideal: 'environment' } } };
  }
  if (attempt === 1) {
    return { audio: false, video: true };
  }
  // Last resort: a device id resolved from enumerateDevices, which only carries
  // usable labels once permission has already been granted.
  if (rearDeviceId) {
    return { audio: false, video: { deviceId: { exact: rearDeviceId } } };
  }
  return { audio: false, video: true };
}

/** Picks the rear camera out of an enumerateDevices() list, by label convention. */
export function pickRearCamera(
  devices: { kind: string; deviceId: string; label: string }[],
): string | undefined {
  const cameras = devices.filter((device) => device.kind === 'videoinput' && device.deviceId);
  if (cameras.length === 0) return undefined;
  const rear = cameras.find((device) => /back|rear|environment/i.test(device.label));
  // iOS orders cameras front-first, so the last entry is the usual fallback.
  return (rear ?? cameras[cameras.length - 1])?.deviceId;
}

export interface CameraDiagnostics {
  userAgent: string;
  standalone: boolean;
  isSecureContext: boolean;
  cameraSupported: boolean;
  trackLabel: string | null;
  trackReadyState: string | null;
  trackMuted: boolean | null;
  settings: Record<string, unknown> | null;
  videoWidth: number;
  videoHeight: number;
  videoReadyState: number;
  paused: boolean;
  attempt: number;
  outcome: string;
}

/**
 * Builds the record that makes a field failure diagnosable.
 *
 * This is logged, never rendered: a supervisor should see "Camera unavailable — Restart
 * Camera", not a device fingerprint.
 */
export function describeCamera(input: {
  snapshot: VideoSnapshot;
  trackLabel?: string | null;
  settings?: Record<string, unknown> | null;
  userAgent?: string;
  standalone?: boolean;
  isSecureContext?: boolean;
  cameraSupported?: boolean;
  attempt?: number;
  outcome?: string;
}): CameraDiagnostics {
  const { snapshot } = input;
  return {
    userAgent: input.userAgent ?? '',
    standalone: input.standalone ?? false,
    isSecureContext: input.isSecureContext ?? false,
    cameraSupported: input.cameraSupported ?? false,
    trackLabel: input.trackLabel ?? null,
    trackReadyState: snapshot.trackReadyState,
    trackMuted: snapshot.trackMuted,
    settings: input.settings ?? null,
    videoWidth: snapshot.videoWidth,
    videoHeight: snapshot.videoHeight,
    videoReadyState: snapshot.readyState,
    paused: snapshot.paused,
    attempt: input.attempt ?? 0,
    outcome: input.outcome ?? 'unknown',
  };
}
